import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type ProposalRequestAccessJSON = z.infer<typeof ProposalRequestAccessSchema.ProposalRequestAccessJSONZ>;

export type ProposalRequestAccessParams = z.infer<typeof ProposalRequestAccessSchema.ProposalRequestAccessZ>;

export default class ProposalRequestAccessSchema {
  public static readonly ProposalRequestAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.ProposalRequestAccess),
    requestId: z.uuid().nullish(),
    accessLevel: z.enum(AccessLevel).nullish(),
  });

  public static readonly ProposalRequestAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.ProposalRequestAccess),
    requestId: z.instanceof(Guid).optional(),
    accessLevel: z.enum(AccessLevel).optional(),
  });

  public static validate(values: unknown): ProposalRequestAccessParams {
    return parseOrThrow(ProposalRequestAccessSchema.ProposalRequestAccessZ, values, {
      logError: true,
      schemaName: "ProposalRequestAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): ProposalRequestAccessJSON {
    return parseOrThrow(ProposalRequestAccessSchema.ProposalRequestAccessJSONZ, values, {
      logError: true,
      schemaName: "ProposalRequestAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is ProposalRequestAccessParams {
    try {
      ProposalRequestAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is ProposalRequestAccessJSON {
    try {
      ProposalRequestAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: ProposalRequestAccessJSON): ProposalRequestAccessParams {
    const parsed = parseOrThrow(ProposalRequestAccessSchema.ProposalRequestAccessJSONZ, data, {
      logError: true,
      schemaName: "ProposalRequestAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return ProposalRequestAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: ProposalRequestAccessParams): ProposalRequestAccessJSON {
    const parsed = ProposalRequestAccessSchema.validate(notification);
    return ProposalRequestAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    ProposalRequestAccessSchema.ProposalRequestAccessJSONZ,
    ProposalRequestAccessSchema.ProposalRequestAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.ProposalRequestAccess as const,
        requestId: Guid.fromJSON(data.requestId) ?? undefined,
        accessLevel: data.accessLevel ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.ProposalRequestAccess as const,
        requestId: resource.requestId?.toJSON(),
        accessLevel: resource.accessLevel,
      }),
    }
  );
}
